import { signTypedData, switchChain } from '@wagmi/core';
import { type Address } from 'viem';

import { computed, type ContractSettings, EvmItemType, useProviderConfig, useState } from '#imports';
import { useAlertsStore } from '~/stores/alerts/useAlertsStore';
import { useUserStore } from '~/stores/user/useUserStore';
import { type EvmConsideration, type EvmOtherRecipientType } from '~/service/EVM/types';
import { useNuxtApp } from '#app';
import { AsyncState } from '~/types/enums';
import type { EvmMarketplace } from '~/service/EVM/config/types';
import { NULL_ADDRESS } from '~/service/EVM/consts';
import { useConfig } from '@wagmi/vue';

type ListingType = {
    paramNetwork: User['network'];
    itemType: AppWTFNft['itemType'];
    value: number;
    endTime: string;
    amount?: number;
    marketContract: ContractSettings;
    marketplace?: EvmMarketplace;
    identifier: string;
    contract: Address;
    otherRecipients: EvmOtherRecipientType[];
    uuid: AppWTFNft['uuid'];
    currencyItemType: EvmItemType;
    currencyTokenAddress: Address;
};

const initialLoadingState = {
    status: AsyncState.IDLE,
    uuid: null
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function customStringify(value: any): string {
    return JSON.stringify(value, (_, v) =>
        typeof v === 'bigint' ? v.toString() : v
    );
}

export function useListing(paramUuid?: string) {
    const {
        map: { chainMap, chainMapById },
        contract: { wrap },
        abi: { type }
    } = useProviderConfig()(['map', 'contract', 'abi']);

    const { $provider } = useNuxtApp();
    const evmCore = useEVMCore();
    const { order: orderApi } = useEVMApi();

    const listingService = evmCore.createEvmListing({
        ...$provider,
        signTypedData,
        chainMapById,
        approveAbi: type
    });

    const alertStore = useAlertsStore();

    const userStore = useUserStore();
    const config = useConfig();

    const loadingState = useState<{
        status: AsyncState;
        uuid: Nullable<AppWTFNft['uuid']>;
    }>(() => initialLoadingState);

    const resetLoadingState = (status?: AsyncState) => {
        if (status) loadingState.value.status = status;
        const resetLoadingState = setTimeout(
            () => loadingState.value = {
                status: AsyncState.IDLE,
                uuid: null
            },
            1000
        );
        clearTimeout(resetLoadingState);
    };

    const createListingOrder = async ({
        paramNetwork,
        value,
        marketplace,
        contract,
        identifier,
        uuid,
        marketContract,
        itemType,
        endTime,
        amount,
        otherRecipients,
        currencyItemType,
        currencyTokenAddress
    }: ListingType) => {
        if (userStore.user && userStore.chainId && marketplace && chainMap[paramNetwork]) {
            loadingState.value = {
                status: AsyncState.IS_LOADING,
                uuid
            };

            if (!chainMap[paramNetwork]) {
                throw new Error('Unsupported network');
            }

            if (userStore.chainId !== chainMap[paramNetwork].id) {
                try {
                    await switchChain(config, { chainId: chainMap[paramNetwork].id });
                }
                catch (e) {
                    throw new Error('Network change error');
                }
            }

            const orderBuildPre = customStringify({
                price: value,
                paramChainId: chainMap[paramNetwork].id,
                userAddress: userStore.user,
                marketplace,
                contract,
                identifier,
                itemType,
                currencyItemType,
                currencyTokenAddress,
                otherRecipients,
                endTime,
                amount
            });

            try {
                const { order, signature } = await listingService.eventEmitter.emit('buildOrder', async () => {
                    if (!userStore.user) userStore.showUserIsDisconnect();
                    return await listingService.buildOrder({
                        price: value,
                        paramChainId: chainMap[paramNetwork].id,
                        userAddress: userStore.user as Address,
                        marketplace,
                        marketContract,
                        contract,
                        identifier,
                        itemType,
                        currencyItemType,
                        currencyTokenAddress,
                        otherRecipients,
                        endTime,
                        amount
                    });
                }, {
                    isApprove: true
                });

                const orderBuild = customStringify(order);

                const firstOffer = order.offer[0];

                if (firstOffer) {
                    await orderApi.fillOrder({
                        user: userStore.user,
                        chain: paramNetwork
                    }, {
                        parameters: {
                            ...order,
                            startTime: String(order.startTime),
                            endTime: String(order.endTime),
                            consideration: order.consideration.map(item => ({
                                token: item.token as Address,
                                recipient: item.recipient as Address,
                                itemType: item.itemType,
                                endAmount: String(item.endAmount),
                                startAmount: String(item.startAmount),
                                identifierOrCriteria: String(item.identifierOrCriteria)
                            }) as EvmConsideration & { price: number }),
                            offer: [{
                                token: firstOffer.token as Address,
                                itemType: firstOffer.itemType,
                                endAmount: String(firstOffer.endAmount),
                                startAmount: String(firstOffer.startAmount),
                                identifierOrCriteria: String(firstOffer.identifierOrCriteria)
                            }]
                        },
                        protocolAddress: NULL_ADDRESS,
                        signature
                    });
                }

                resetLoadingState(AsyncState.IS_SUCCESS);
                alertStore.add({
                    title: 'Success!',
                    description: 'Your offer has been successfully submitted',
                    critical: false,
                    status: 'type.success'
                });
            }
            catch (err) {
                const error = err as Error;
                resetLoadingState(AsyncState.IS_ERROR);
                return alertStore.add({
                    title: 'Oops, something went wrong',
                    description: `${error.message}`,
                    critical: false,
                    status: 'type.error'
                });
            }
        }
        else {
            userStore.showUserIsDisconnect();
        }
    };
    return {
        createListingOrder,
        loadingState,
        isError: computed(() => !!paramUuid && loadingState.value.uuid === paramUuid && loadingState.value.status === AsyncState.IS_ERROR),
        isSuccess: computed(() => !!paramUuid && loadingState.value.uuid === paramUuid && loadingState.value.status === AsyncState.IS_SUCCESS),
        isLoading: computed(() => !!paramUuid && loadingState.value.uuid === paramUuid && loadingState.value.status === AsyncState.IS_LOADING)
    };
}
